import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { markRivhitDocumentsDirty, rememberRivhitDocumentLink } from '../lib/rivhitDocumentsSync';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Token storage
let authToken: string | null = null;

export function setAuthToken(token: string | null) {
  authToken = token;
  if (token) {
    localStorage.setItem('auth_token', token);
  } else {
    localStorage.removeItem('auth_token');
  }
}

export function getAuthToken(): string | null {
  if (!authToken) {
    authToken = localStorage.getItem('auth_token');
  }
  return authToken;
}

export function getAuthHeaders(): Record<string, string> {
  const token = getAuthToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

export function clearAuthToken() {
  authToken = null;
  localStorage.removeItem('auth_token');
}

// Callback for 401 errors — AuthContext registers a handler to clear user state
// without causing a full page reload.
let _onUnauthorized: (() => void) | null = null;
export function setOnUnauthorized(handler: (() => void) | null) {
  _onUnauthorized = handler;
}

// Request interceptor - add auth token and timing metadata
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    (config as any)._requestStartMs = performance.now();
    console.log(`[API] ➡️  ${config.method?.toUpperCase()} ${config.url}`, { params: config.params, timestamp: new Date().toISOString() });
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle auth errors and log timing
api.interceptors.response.use(
  (response) => {
    const startMs = (response.config as any)._requestStartMs as number | undefined;
    const elapsed = startMs != null ? (performance.now() - startMs).toFixed(1) : '?';
    const itemCount = Array.isArray(response.data) ? response.data.length
      : Array.isArray(response.data?.items) ? response.data.items.length
      : undefined;
    console.log(
      `[API] ✅  ${response.config.method?.toUpperCase()} ${response.config.url} — ${response.status} in ${elapsed}ms`,
      itemCount != null ? { items: itemCount, timestamp: new Date().toISOString() } : { timestamp: new Date().toISOString() },
    );
    return response;
  },
  (error: AxiosError) => {
    const startMs = (error.config as any)?._requestStartMs as number | undefined;
    const elapsed = startMs != null ? (performance.now() - startMs).toFixed(1) : '?';
    console.error(
      `[API] ❌  ${error.config?.method?.toUpperCase()} ${error.config?.url} — ${error.response?.status ?? 'network error'} in ${elapsed}ms`,
      { timestamp: new Date().toISOString() },
    );
    if (error.response?.status === 401) {
      clearAuthToken();
      if (_onUnauthorized) {
        _onUnauthorized();
      }
    }
    return Promise.reject(error);
  }
);

// ==================
// Auth API
// ==================

export interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
}

export interface AuthResponse {
  access_token: string;
  user: User;
}

export async function googleAuth(googleToken: string): Promise<AuthResponse> {
  const response = await api.post<AuthResponse>('/auth/google', { token: googleToken });
  return response.data;
}

export async function getCurrentUser(): Promise<User> {
  const response = await api.get<User>('/auth/me');
  return response.data;
}

export async function logout(): Promise<void> {
  await api.post('/auth/logout');
  clearAuthToken();
}

export const SKIP_AUTH = import.meta.env.VITE_SKIP_AUTH === 'true';

export async function devLogin(): Promise<AuthResponse> {
  const response = await api.post<AuthResponse>('/auth/dev-login');
  return response.data;
}

// ==================
// Overview
// ==================
export const getOverview = () => api.get('/overview');

// ==================
// Chats
// ==================
export const listChats = (monitoredOnly: boolean = true) => api.get('/chats', { params: { monitored_only: monitoredOnly } });
export const getChat = (chatId: string) => api.get(`/chats/${chatId}`);
export const createChat = (data: any) => api.post('/chats', data);
export const updateChat = (chatId: string, data: any) => api.patch(`/chats/${chatId}`, data);
export const deleteChat = (chatId: string) => api.delete(`/chats/${chatId}`);
export const discoverChats = (chats: any[]) => api.post('/chats/discover', chats);
export const getChatSignals = (chatId: string) => api.get(`/chats/${chatId}/signals`);
export const getChatMessages = (chatId: string, params?: any) => api.get(`/chats/${chatId}/messages`, { params });

// ==================
// Messages
// ==================
export const ingestMessages = (data: any) => api.post('/messages/ingest', data);
export const listMessagesByChat = (chatId: string, params?: any) => api.get(`/messages/${chatId}`, { params });
export const getMessage = (messageId: string) => api.get(`/messages/detail/${messageId}`);

// ==================
// Signals
// ==================
export const listSignals = (params?: any) => api.get('/signals', { params });
export const getSignal = (signalId: string) => api.get(`/signals/${signalId}`);
export const updateSignal = (signalId: string, data: any) => api.patch(`/signals/${signalId}`, data);
export const confirmSignal = (signalId: string) => api.post(`/signals/${signalId}/confirm`);
export const dismissSignal = (signalId: string) => api.post(`/signals/${signalId}/dismiss`);
export const convertSignalToReminder = (signalId: string) => api.post(`/signals/${signalId}/convert-reminder`);
export const convertSignalToEntity = (signalId: string) => api.post(`/signals/${signalId}/convert-entity`);
export const bulkSignalAction = (data: any) => api.post('/signals/bulk', data);

// ==================
// Reminders
// ==================
export const listReminders = (params?: any) => api.get('/reminders', { params });
export const getReminder = (reminderId: string) => api.get(`/reminders/${reminderId}`);
export const createReminder = (data: any) => api.post('/reminders', data);
export const updateReminder = (reminderId: string, data: any) => api.patch(`/reminders/${reminderId}`, data);
export const snoozeReminder = (reminderId: string, data: any) => api.post(`/reminders/${reminderId}/snooze`, data);
export const completeReminder = (reminderId: string) => api.post(`/reminders/${reminderId}/complete`);
export const dismissReminder = (reminderId: string) => api.post(`/reminders/${reminderId}/dismiss`);

// ==================
// Entities
// ==================
export const listEntities = (params?: any) => api.get('/entities', { params });
export const getEntity = (entityId: string) => api.get(`/entities/${entityId}`);
export const createEntity = (data: any) => api.post('/entities', data);
export const updateEntity = (entityId: string, data: any) => api.patch(`/entities/${entityId}`, data);
export const linkSignalToEntity = (entityId: string, signalId: string) => api.post(`/entities/${entityId}/link-signal`, { signal_id: signalId });
export const mergeEntities = (data: any) => api.post('/entities/merge', data);

// ==================
// Review
// ==================
export const listReviewItems = (params?: any) => api.get('/review', { params });
export const getReviewItem = (itemId: string) => api.get(`/review/${itemId}`);
export const reviewAction = (itemId: string, data: any) => api.post(`/review/${itemId}/action`, data);

// ==================
// Settings
// ==================
export const getSettings = () => api.get('/settings');
export const updateSettings = (data: any) => api.patch('/settings', data);
export const getChatRules = () => api.get('/settings/chat-rules');
export const setChatRules = (chatId: string, data: any) => api.put(`/settings/chat-rules/${chatId}`, data);
export const getRivhitConfig = () => api.get('/settings/rivhit');
export const updateRivhitConfig = (data: { api_token?: string; username?: string; password?: string; id_company?: string }) => api.put('/settings/rivhit', data);
export const testRivhitConfig = (data: { api_token?: string; username?: string; password?: string; id_company?: string }) => api.post('/settings/rivhit/test', data);
export const getWhatsAppPhoneLinkStatus = () => api.get('/settings/whatsapp-phone');
export const startWhatsAppPhoneLink = (data: { phone_number: string }) => api.post('/settings/whatsapp-phone/start', data);
export const confirmWhatsAppPhoneLink = (data: { phone_number: string; code: string }) => api.post('/settings/whatsapp-phone/confirm', data);
export const deleteWhatsAppPhoneLink = () => api.delete('/settings/whatsapp-phone');

// ==================
// Search
// ==================
export const globalSearch = (data: any) => api.post('/search', data);

// ==================
// Orders
// ==================
export const analyzeOrders = (data: { chat_id?: string; chat_ids?: string[]; hours: number }) => api.post('/orders/analyze', data);
export const extractCustomers = (data: { chat_id?: string; chat_ids?: string[]; hours: number }) => api.post('/orders/extract-customers', data);
export const extractItems = (data: { thread_id: string; confirmed_customers: Array<{ customer_id: string; customer_name: string; source_message_indices: number[] }> }) => api.post('/orders/extract-items', data);
export const listOrders = (params?: { chat_id?: string; status?: string; limit?: number }) => api.get('/orders', { params });
export const getOrder = (orderId: string) => api.get(`/orders/${orderId}`);
export const updateOrder = (orderId: string, data: any) => api.patch(`/orders/${orderId}`, data);
export const deleteOrder = (orderId: string) => api.delete(`/orders/${orderId}`);
export const reevaluateOrder = (orderId: string, data: { customer_id: string; customer_name: string }) => api.post(`/orders/${orderId}/reevaluate`, data);
export const getOrderSourceMessages = (orderId: string) => api.get(`/orders/${orderId}/source-messages`);
export const previewMessages = (chatId: string, hours: number) => api.get('/orders/messages-preview', { params: { chat_id: chatId, hours } });
export const getChatsWithMessages = () => api.get('/orders/chats-with-messages');
export const fetchWhatsAppMessages = (chatId: string) => api.post('/orders/fetch-whatsapp-messages', { chat_id: chatId, hours: 24 });
export const syncWhatsAppHistory = (chatId: string) => api.post('/orders/sync-history', { chat_id: chatId, hours: 24 });
export const resolvePrices = (data: { customer_id: string; items: Array<{ product_id: string; product_name?: string }> }) =>
  api.post<{ resolved: Array<{ product_id: string; product_name?: string; price?: number; price_source?: string; needs_manual_input: boolean; last_purchased_at?: string; catalog_price?: number }> }>('/orders/resolve-prices', data);
export const listOrderSuggestions = (limit: number = 10) => api.get('/orders/suggestions', { params: { limit } });
export const runOrderSuggestions = (chatId: string) => api.post('/orders/suggestions/run', { chat_id: chatId, hours: 2 });
export const dismissOrderSuggestion = (suggestionId: string) => api.post(`/orders/suggestions/${suggestionId}/dismiss`);
export const approveOrderSuggestion = (
  suggestionId: string,
  data: { document_type: number; document_number: number; document_link?: string; customer_id?: number | null }
) => api.post(`/orders/suggestions/${suggestionId}/approve`, data);

// ==================
// Customers
// ==================
export const listCustomers = (limit?: number) => api.get('/customers', { params: { limit } });
export const getCustomer = (customerId: string) => api.get(`/customers/${customerId}`);
export const searchCustomers = (q: string, limit?: number) => api.get('/customers/search', { params: { q, limit } });
export const getCustomerDebtReport = (customerId: number, params?: Record<string, string>) => api.get(`/reports/customers/${customerId}`, { params });

// ==================
// Products
// ==================
export const listProducts = (params?: { category?: string; limit?: number }) => api.get('/products', { params });
export const getProduct = (productId: string) => api.get(`/products/${productId}`);
export const searchProducts = (q: string, limit?: number) => api.get('/products/search', { params: { q, limit } });

// ==================
// Order History
// ==================
export const getCustomerOrderProfile = (customerId: string) => api.get(`/order-history/${customerId}`);

// ==================
// Invoicing (Rivhit)
// ==================
export const getInvoicingSummary = () => api.get('/invoicing/summary');
export const getDocumentTypes = () => api.get('/invoicing/document-types');
export const getReceiptTypes = () => api.get('/invoicing/receipt-types');
export const getPaymentTypes = () => api.get('/invoicing/payment-types');
export const getRivhitCustomers = () => api.get('/invoicing/customers');
export const createRivhitCustomer = (data: {
  last_name: string;
  first_name?: string;
  phone?: string;
  email?: string;
  city?: string;
  street?: string;
  id_number?: number;
  vat_number?: number;
}) => api.post<{ customer_id: number }>('/invoicing/customers', data);
export const getRivhitItems = () => api.get('/invoicing/items');
export const getRivhitItemPricing = (itemId: number, customerId: number) =>
  api.get('/invoicing/items/' + itemId + '/pricing', { params: { customer_id: customerId } });
export const getCustomerRecentItems = (() => {
  const inflight = new Map<number, Promise<{ data: { item_ids: number[] } }>>();
  return (customerId: number) => {
    const existing = inflight.get(customerId);
    if (existing) return existing;
    const promise = api
      .get<{ item_ids: number[] }>('/invoicing/customers/' + customerId + '/recent-items')
      .finally(() => inflight.delete(customerId));
    inflight.set(customerId, promise);
    return promise;
  };
})();
export const getVatRate = () => api.get('/invoicing/vat-rate');
export const listInvoices = (params?: Record<string, unknown>) => api.get('/invoicing/documents', { params });
export const createInvoice = async (data: Record<string, unknown>) => {
  const response = await api.post('/invoicing/documents', data);
  console.log(
    `[RivhitDocs] createInvoice response type=${response.data?.document_type} ` +
    `number=${response.data?.document_number} link=${response.data?.document_link || 'NONE'} ` +
    `ts=${new Date().toISOString()}`
  );
  rememberRivhitDocumentLink(
    response.data?.document_type,
    response.data?.document_number,
    response.data?.document_link,
  );
  markRivhitDocumentsDirty();
  return response;
};
export const listReceipts = (params?: Record<string, unknown>) => api.get('/invoicing/receipts', { params });
export const createReceipt = async (data: Record<string, unknown>) => {
  const response = await api.post('/invoicing/receipts', data);
  console.log(
    `[RivhitDocs] createReceipt response type=${response.data?.document_type} ` +
    `number=${response.data?.document_number} link=${response.data?.document_link || 'NONE'} ` +
    `ts=${new Date().toISOString()}`
  );
  rememberRivhitDocumentLink(
    response.data?.document_type,
    response.data?.document_number,
    response.data?.document_link,
  );
  markRivhitDocumentsDirty();
  return response;
};

// Unified documents (all types combined)
export const listAllDocuments = (params?: {
  from_date?: string;
  to_date?: string;
  customer_name?: string;
  customer_id?: number;
  category_filter?: string;
  open_only?: boolean;
  group_by?: string;
  include_children?: boolean;
  limit?: number;
  offset?: number;
}) => api.get('/invoicing/all-documents', { params });

export const getDocumentDetails = (documentType: number, documentNumber: number) =>
  api.get<{ document_type: number; document_number: number; items: Array<{ item_id?: number; description?: string; catalog_number?: string; quantity: number; price_nis?: number; total_nis?: number }> }>(
    `/invoicing/documents/${documentType}/${documentNumber}/details`
  );

// ==================
// Reports
// ==================
export const getReport = (params?: { period?: string; from_date?: string; to_date?: string }) =>
  api.get('/reports', { params });

// ==================
// Agent Chat
// ==================
export const getAgentChatUrl = () => `${API_BASE_URL}/agent/chat`;
export const getAgentCardResponseUrl = () => `${API_BASE_URL}/agent/card-response`;

export const listAgentConversations = () => api.get('/agent/conversations');
export const getAgentConversation = (id: string) => api.get(`/agent/conversations/${id}`);
export const deleteAgentConversation = (id: string) => api.delete(`/agent/conversations/${id}`);

// ==================
// WhatsApp Bridge
// ==================
export const getWhatsAppBridgeStatus = () =>
  api.get('/whatsapp-bridge/status').then(res => res.data);

export const getWhatsAppBridgeQr = () =>
  api.get('/whatsapp-bridge/qr').then(res => res.data);

export const connectWhatsAppBridge = (fresh = false) =>
  api.post(`/whatsapp-bridge/connect?fresh=${fresh}`).then(res => res.data);

export const disconnectWhatsAppBridge = (fullWipe = false) =>
  api.post(`/whatsapp-bridge/disconnect?full_wipe=${fullWipe}`).then(res => res.data);

export const discoverWhatsAppBridge = () =>
  api.get('/whatsapp-bridge/discover').then(res => res.data);

export const refreshMonitoredChats = () =>
  api.post('/whatsapp-bridge/refresh-monitored').then(res => res.data);

export default api;
